import { State } from '@progress/kendo-data-query';
import { isWithinInterval } from 'date-fns';
import { TFunction } from 'i18next';
import { isEmpty, isEqual } from 'lodash';
import moment from 'moment-timezone';
import { FC, ReactNode, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import uuid from 'react-uuid';
import useBoolean from 'src/hooks/useBoolean';
import usePostMessage from 'src/hooks/usePostMessage';
import useGLPeriodApi, { GLPeriods, GlPeriodObject } from 'src/services/useGLPeriodApi';
import useGlCodeSummaryApi, { GlCodeSummaryResponse } from 'src/services/useGlCodeSummaryApi';
import useGLCodesBudgetApi, {
  GLCodesBudgets,
  IGLCodesBudgetApiProps,
} from 'src/services/useGlCodesBudgetApi';
import useMuliPeriodAllocationGetApi, {
  BudgetPeriodResponse,
} from 'src/services/useMultiPeriodAllocationGetApi';
import usePoDetailsApi, { PoDetails } from 'src/services/usePoDetailsApi';
import { IBudgetPeriod } from 'src/types/BudgetPeriod';
import { SetState } from 'src/types/generic';
import { lastIndex, round } from 'src/utils';
import { removeTime } from 'src/utils/formatTime';
import { GLPeriodDropdown, formatGLPeriodDropDown } from 'src/utils/transformation';
import useViewBudgetTableErrors, {
  BudgetLineItemError,
  BudgetLineItemInfo,
  BudgetLineItemWarning,
} from './useViewBudgetTableErrors';

interface ViewBudgetPeriodContextType {
  dropDownValues: GLPeriodDropdown[];
  isDropDownLoading: boolean;
  glPeriods: GLPeriods;
  companyId?: string;
  expand: boolean;
  toggleExpand: () => void;
  budgetPeriods: IBudgetPeriod[];
  setBudgetPeriods: SetState<IBudgetPeriod[]>;
  distributionType: 'Percentage' | 'Amount';
  setDistributionType: SetState<'Percentage' | 'Amount'>;
  poId: string;
  poDetails: PoDetails;
  errors: BudgetLineItemError[];
  warnings: BudgetLineItemWarning[];
  infos: BudgetLineItemInfo[];
  budgetPeriodDataWithErrors: Record<string, any>[];
  glCodeSummary: GlCodeSummaryResponse[];
  isPoDetailsLoading: boolean;
  isSummaryLoading: boolean;
  isBudgetPeriodLoading: boolean;
  currentBudgetPeriod: GlPeriodObject;
  totalPercentage: number;
  totalAmount: number;
  errorMessage: ErrorMessage;
  errorMessageList: string[];
  warningList: string[];
  informationList: string[];
  errorList: string[];
  saveErrorList: string[];
  hasWarnings: boolean;
  hasErrors: boolean;
  isReadOnly: boolean;
  refreshBudgetPeriods: (params?: undefined) => Promise<{
    periodAllocations: BudgetPeriodResponse[];
    canEditPo: boolean;
  }>;
  offlinePo: boolean;
  isBudgetPeriodDirty: boolean;
  footerInfoMessage: string[];
  getGlCodesBudget: (params?: IGLCodesBudgetApiProps | undefined) => Promise<GLCodesBudgets>;
  initialCheckbookState: State;
  t: TFunction<'translation', undefined>;
}

export interface ErrorMessage {
  totalAmount: string[];
  totalPercentage: string[];
}

export const ViewBudgetPeriodContext = createContext<ViewBudgetPeriodContextType>(
  {} as ViewBudgetPeriodContextType
);

interface ViewBudgetPeriodContextProps {
  children: ReactNode;
}

function getAllFuturePeriods(array: GLPeriods, index: number) {
  const futurePeriods = array.slice(0, index + 1);
  return futurePeriods;
}

const initialCheckbookState: State = {
  skip: 0,
  take: 20,
  sort: [],
  filter: {
    logic: 'and',
    filters: [],
  },
};

const currentDate = removeTime(new Date());

const ViewBudgetPeriodContextProvider: FC<ViewBudgetPeriodContextProps> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const poId = useMemo(() => searchParams.get('poId') || '', [searchParams]);
  const { poDetails, isLoading: isPoDetailsLoading } = usePoDetailsApi(poId);
  const { isOffline = false, glPeriodId } = useMemo(() => poDetails || {}, [poDetails]);
  const { glCodeSummary = [], isSummaryLoading } = useGlCodeSummaryApi(poId);
  const companyId = useMemo(() => poDetails?.companyId?.toString(), [poDetails?.companyId]);
  const { getGLPeriods, glPeriods, isLoading: isDropDownLoading } = useGLPeriodApi(companyId);
  const { getGlCodesBudget } = useGLCodesBudgetApi(companyId);

  const getLastYearPeriod = useCallback((array: GLPeriods) => {
    const lastYear = moment().subtract(1, 'year').year();
    const index = lastIndex(array, (value) => value.year === lastYear);
    if (index === -1) {
      return array.slice(0, array.length);
    }
    return array.slice(0, index + 1);
  }, []);

  const { t } = useTranslation();

  const initialDropdownIndex: number = useMemo(
    () =>
      glPeriods.findIndex((value) =>
        isWithinInterval(currentDate, {
          start: removeTime(new Date(value.startDate)),
          end: removeTime(new Date(value.endDate)),
        })
      ),
    [glPeriods]
  );

  const currentBudgetPeriod = useMemo(
    () => glPeriods[initialDropdownIndex] ?? ({} as GlPeriodObject),
    [glPeriods, initialDropdownIndex]
  );

  const initialMultiBudgetPeriods: IBudgetPeriod[] = useMemo(() => {
    const currentglPeriodId = glPeriodId || currentBudgetPeriod.glPeriodId;
    const isClosed = glPeriods.find((el) => el?.glPeriodId === currentglPeriodId)?.isClosed;
    return [
      {
        glPeriodId: currentglPeriodId,
        percentageAmount: 100,
        amount: Number(poDetails?.poTotal || 0),
        isDeletable: false,
        isClosed: Number(isClosed),
        rowId: uuid(),
        items:
          glCodeSummary
            ?.filter((summary) => summary.glPeriodId === currentglPeriodId)
            ?.map((item) => ({
              ...item,
              distributionPercentage: 100,
              distributionAmount: Number(item?.totalAmount),
            })) || [],
        expanded: false,
      },
    ];
  }, [currentBudgetPeriod.glPeriodId, glCodeSummary, glPeriodId, glPeriods, poDetails?.poTotal]);

  const {
    multiBudgetPeriods,
    isBudgetPeriodLoading,
    getMultiBudgetPeriods: refreshBudgetPeriods,
    canEditPeriods,
  } = useMuliPeriodAllocationGetApi(poId, initialMultiBudgetPeriods);

  const isReadOnly = !canEditPeriods;
  const [budgetPeriods, setBudgetPeriods] = useState<IBudgetPeriod[]>([]);
  const { value: expand, toggle: toggleExpand } = useBoolean(false);
  const [distributionType, setDistributionType] = useState<'Percentage' | 'Amount'>('Percentage');

  const isBudgetPeriodDirty = useMemo(() => {
    const multiPeriodData = budgetPeriods.map((item) => ({
      glPeriodId: item.glPeriodId,
      amount: Number(item.amount),
    }));
    const multiBudgetPeriodsRows = multiBudgetPeriods.map((item) => ({
      glPeriodId: item.glPeriodId,
      amount: Number(item.amount),
    }));
    return !isEqual(multiPeriodData, multiBudgetPeriodsRows);
  }, [budgetPeriods, multiBudgetPeriods]);

  useEffect(() => {
    if (isBudgetPeriodDirty) {
      setBudgetPeriods((prev) => [...prev].map((period) => ({ ...period, expanded: false })));
    }
  }, [isBudgetPeriodDirty]);

  const optionsOnPoStatus = useMemo(() => {
    if (initialDropdownIndex === -1) {
      return glPeriods;
    }
    if (isOffline) {
      return getLastYearPeriod(glPeriods);
    } else {
      return getAllFuturePeriods(glPeriods, initialDropdownIndex);
    }
  }, [getLastYearPeriod, glPeriods, initialDropdownIndex, isOffline]);

  const formattedDropDownValues = useMemo(() => formatGLPeriodDropDown(glPeriods), [glPeriods]);

  const dropDownValues = useMemo(() => {
    const formattedData = formatGLPeriodDropDown(optionsOnPoStatus);
    let dropDownValues: GLPeriodDropdown[] = [];

    formattedData.forEach((item) => {
      formattedDropDownValues.forEach((value) => {
        if (item.fiscalYear === value.fiscalYear) {
          const newValue: GLPeriodDropdown = {
            ...value,
            items: value.items.filter((p) => item.items.find((v) => v.glPeriodId === p.glPeriodId)),
          };
          dropDownValues.push(newValue);
        }
      });
    });
    return dropDownValues;
  }, [formattedDropDownValues, optionsOnPoStatus]);

  const { totalPercentage, totalAmount, errorMessage, footerInfoMessage } = useMemo(() => {
    let errorMessage: ErrorMessage = {
      totalAmount: [],
      totalPercentage: [],
    };
    let footerInfoMessage: string[] = [];
    let totalPercentage = 0;
    let totalAmount = 0;
    const poTotal = poDetails?.poTotal || 0;

    budgetPeriods.forEach((item) => {
      const percentageAmount = Number(item.percentageAmount) || 0;
      const amount = Number(item.amount) || 0;

      totalPercentage += percentageAmount;
      totalAmount += amount;
    });
    totalPercentage = round(totalPercentage, 2);
    totalAmount = round(totalAmount, 2);
    const roundedPercentage = round(totalPercentage);

    if (roundedPercentage !== 100) {
      errorMessage.totalPercentage = ['Total % Distribution must be equal to 100%'];
    }
    if (totalAmount !== poTotal) {
      errorMessage.totalAmount = ['Total $ Distribution must be equal to Total Amount'];
    }
    if (totalPercentage !== 100 && roundedPercentage === 100) {
      footerInfoMessage = [
        '100% of the $ value has been allocated. \n% Distribution values have been rounded.',
      ];
    }

    return { totalPercentage, totalAmount, errorMessage, footerInfoMessage };
  }, [budgetPeriods, poDetails?.poTotal]);

  const { errors, warnings, infos, saveErrors } = useViewBudgetTableErrors(
    budgetPeriods,
    distributionType,
    glPeriods,
    isReadOnly
  );
  const warningList: string[] = [
    ...Object.values(warnings)
      .map((i) => Object.values(i)?.flat())
      .flat()
      .map((item: any) => (typeof item === 'string' ? item : Object.values(item)?.flat()))
      .flat()
      .filter((item: any, i, arr) => arr.indexOf(item) === i),
  ] as string[];

  const informationList: string[] = [
    ...Object.values(infos)
      .map((i) => Object.values(i)?.flat())
      .flat()
      .map((item: any) => (typeof item === 'string' ? item : Object.values(item)?.flat()))
      .flat()
      .filter((item: any, i, arr) => arr.indexOf(item) === i),
  ] as string[];

  const errorList: string[] = [
    ...Object.values(errors)
      .map((i) => Object.values(i)?.flat())
      .flat()
      .map((item: any) => (typeof item === 'string' ? item : Object.values(item)?.flat()))
      .flat()
      .filter((item: any, i, arr) => arr.indexOf(item) === i),
  ] as string[];

  const saveErrorList: string[] = [
    ...Object.values(saveErrors)
      .map((i) => Object.values(i)?.flat())
      .flat()
      .map((item: any) => (typeof item === 'string' ? item : Object.values(item)?.flat()))
      .flat()
      .filter((item: any, i, arr) => arr.indexOf(item) === i),
  ] as string[];

  const errorMessageList = [
    ...errorMessage.totalAmount,
    ...errorMessage.totalPercentage,
  ] as string[];

  const hasWarnings = useMemo(() => !!warningList.length, [warningList.length]);
  const hasErrors = useMemo(
    () => !!errorList.length || !!errorMessageList.length,
    [errorList.length, errorMessageList.length]
  );

  const budgetPeriodDataWithErrors: IBudgetPeriod[] = useMemo(
    () =>
      budgetPeriods.map((item, index) => ({
        ...item,
        errors: errors?.[index],
        warnings: warnings?.[index],
        informations: infos?.[index],
        hasErrorsOrWarnings:
          Object.values(errors?.[index])?.flat()?.length > 0 ||
          Object.values(warnings?.[index])?.flat()?.length > 0 ||
          Object.values(infos?.[index])?.flat()?.length > 0 ||
          false,
      })),
    [budgetPeriods, errors, infos, warnings]
  );

  const calculateBudgetPeriod: IBudgetPeriod[] = useMemo(() => {
    if (multiBudgetPeriods && glCodeSummary && poDetails) {
      if (isEmpty(multiBudgetPeriods)) {
        return initialMultiBudgetPeriods;
      } else {
        return multiBudgetPeriods.map((item, indx) => {
          const percentageAmount = round(
            (Number(item.amount) / Number(poDetails.poTotal)) * 100,
            2
          );

          const items = glCodeSummary
            .filter((summary) => summary.glPeriodId === item.glPeriodId)
            .map((data) => ({
              ...data,
              distributionPercentage: percentageAmount,
              distributionAmount: Number(data.totalAmount),
            }));
          return {
            ...item,
            percentageAmount,
            isDeletable: indx !== 0,
            rowId: uuid(),
            expanded: false,
            items,
          };
        });
      }
    }
    return [];
  }, [glCodeSummary, initialMultiBudgetPeriods, multiBudgetPeriods, poDetails]);

  useEffect(() => {
    setBudgetPeriods(calculateBudgetPeriod);
  }, [calculateBudgetPeriod]);

  useEffect(() => {
    if (companyId) getGLPeriods();
  }, [getGLPeriods, companyId]);

  const dirtyMessage = useMemo(() => ({ isDirty: isBudgetPeriodDirty }), [isBudgetPeriodDirty]);
  usePostMessage(dirtyMessage);

  return (
    <ViewBudgetPeriodContext.Provider
      value={{
        companyId,
        dropDownValues,
        isDropDownLoading,
        glPeriods,
        expand,
        toggleExpand,
        budgetPeriods,
        setBudgetPeriods,
        distributionType,
        setDistributionType,
        poId,
        poDetails,
        errors,
        warnings,
        infos,
        budgetPeriodDataWithErrors,
        glCodeSummary,
        isBudgetPeriodLoading,
        isPoDetailsLoading,
        isSummaryLoading,
        currentBudgetPeriod,
        totalPercentage,
        totalAmount,
        errorMessage,
        errorList,
        saveErrorList,
        warningList,
        informationList,
        errorMessageList,
        hasErrors,
        hasWarnings,
        refreshBudgetPeriods,
        isReadOnly,
        offlinePo: isOffline,
        isBudgetPeriodDirty,
        footerInfoMessage,
        getGlCodesBudget,
        initialCheckbookState,
        t,
      }}
    >
      {children}
    </ViewBudgetPeriodContext.Provider>
  );
};

export default ViewBudgetPeriodContextProvider;
